<template>
  <div>
    <div class="d-flex flex-wrap align-items-center border rounded p-2 mb-2" @click="focusInput" @mouseup="actualitzarSeleccio(true)">
      <span v-for="(tag, index) in tags" :key="`${tag.toLowerCase()}-${index}`" class="badge badge-primary mr-2 mb-1 py-0" :class="{ 'tag-selection-highlight': tagsSeleccionats.includes(index) }">
        <span ref="tagLabels" class="tag-label">{{ tag }}</span>
        <button type="button" class="btn btn-sm p-0 ml-1 text-white" :aria-label="`Eliminar ${tag}`" :disabled="disabled" @click.stop="eliminar(index)">
          <i class="fa-solid fa-xmark fa-xs"></i>
        </button>
      </span>
      <input
        ref="input"
        :id="inputId"
        v-model="entrada"
        type="text"
        class="form-control border-0 flex-grow-1"
        :disabled="disabled"
        aria-label="Afegir etiqueta"
        @keydown="tecla"
        @keyup="actualitzarSeleccio()"
        @paste="enganxar"
        @copy="copiarSeleccio"
        @blur="afegirEntrada"
      />
    </div>
    <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="disabled || !tags.length" @click="copiar">
      <i class="fa-solid fa-copy mr-1"></i>Copiar
    </button>
  </div>
</template>

<script>
export default {
  name: "Etiquetes",
  props: {
    value: {
      type: String,
      default: "",
    },
    inputId: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return { entrada: "", tagsSeleccionats: [], seleccioTeclatEtiquetes: false };
  },
  computed: {
    tags() {
      const vistos = new Set();
      return (this.value || "")
        .split(";")
        .map(tag => tag.trim())
        .filter(tag => {
          const clave = tag.toLocaleLowerCase();
          if (!tag || vistos.has(clave)) return false;
          vistos.add(clave);
          return true;
        });
    },
  },
  methods: {
    focusInput() {
      const seleccio = window.getSelection();
      if (seleccio && !seleccio.isCollapsed) return;
      this.$refs.input.focus();
    },
    actualitzarSeleccio(esMouse = false) {
      if (esMouse) this.seleccioTeclatEtiquetes = false;

      const seleccio = window.getSelection();
      if (!seleccio || seleccio.isCollapsed) {
        this.tagsSeleccionats = [];
        return;
      }

      const rang = seleccio.getRangeAt(0);
      this.tagsSeleccionats = (this.$refs.tagLabels || []).reduce((seleccionats, etiqueta, index) => {
        if (rang.intersectsNode(etiqueta)) seleccionats.push(index);
        return seleccionats;
      }, []);
    },
    seleccionarEtiquetes() {
      const etiquetes = this.$refs.tagLabels || [];
      if (!etiquetes.length) return;

      const primerText = etiquetes[0].firstChild;
      const ultimText = etiquetes[etiquetes.length - 1].firstChild;
      const rang = document.createRange();
      rang.setStart(primerText, 0);
      rang.setEnd(ultimText, ultimText.textContent.length);

      const seleccio = window.getSelection();
      seleccio.removeAllRanges();
      seleccio.addRange(rang);
      this.seleccioTeclatEtiquetes = true;
      this.actualitzarSeleccio();
    },
    emitir(tags) {
      this.$emit("input", tags.join(";"));
    },
    afegir(tags) {
      const result = this.tags.slice();
      const vistos = new Set(result.map(tag => tag.toLocaleLowerCase()));

      tags.forEach(tag => {
        const texto = tag.replace(/;/g, " ").trim();
        const clau = texto.toLocaleLowerCase();
        if (texto && !vistos.has(clau)) {
          vistos.add(clau);
          result.push(texto);
        }
      });

      this.emitir(result);
    },
    afegirEntrada() {
      if (!this.entrada.trim()) return;
      this.afegir([this.entrada]);
      this.entrada = "";
    },
    tecla(event) {
      const seleccionarTot = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a" && !this.entrada;
      if (seleccionarTot && this.tags.length) {
        event.preventDefault();
        this.seleccionarEtiquetes();
        return;
      }

      this.seleccioTeclatEtiquetes = false;
      if (event.key === "Enter" || event.key === ";") {
        event.preventDefault();
        this.afegirEntrada();
      }
    },
    enganxar(event) {
      const texto = event.clipboardData.getData("text");
      const separaComes = !/[\r\n\t;]/.test(texto);
      const separador = separaComes ? /[,\r\n\t;]+/ : /[\r\n\t;]+/;

      event.preventDefault();
      this.afegir(texto.split(separador));
      this.entrada = "";
    },
    eliminar(index) {
      this.emitir(this.tags.filter((_, i) => i !== index));
    },
    copiarSeleccio(event) {
      if (!this.tags.length || !event.clipboardData) return;

      if (this.seleccioTeclatEtiquetes) {
        event.clipboardData.setData("text/plain", this.tags.join("\n"));
        event.preventDefault();
        return;
      }

      const entrada = event.target;
      const seleccioEntrada = entrada.selectionStart !== entrada.selectionEnd;
      const seleccioText = window.getSelection();
      if (seleccioEntrada || (seleccioText && !seleccioText.isCollapsed)) return;

      event.clipboardData.setData("text/plain", this.tags.join("\n"));
      event.preventDefault();
    },
    async copiar() {
      const texto = this.tags.join("\n");

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(texto);
        return;
      }

      const auxiliar = document.createElement("textarea");
      auxiliar.value = texto;
      auxiliar.style.position = "fixed";
      auxiliar.style.opacity = "0";
      document.body.appendChild(auxiliar);
      auxiliar.select();
      document.execCommand("copy");
      document.body.removeChild(auxiliar);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";

.tag-selection-highlight {
  background-color: $black !important;
  color: $white !important;
}

.tag-selection-highlight button {
  color: $white !important;
}

.tag-label::selection {
  background-color: $black;
  color: $white;
}
</style>